import React, { useEffect, useState, useCallback, useRef } from "react";
import styled, { keyframes } from "styled-components";
import { fetchMarket, fetchMarketTrades, fetchMarketHistory, fetchMarketVolume } from "./client";
import TradeModal from "./TradeModal";
import CommentsSection from "./CommentsSection";
import MarketDetailBottomBar from "./MarketDetailBottomBar";
import { useI18n } from "./i18n";

const fadeUp = keyframes`from{opacity:0;transform:translateY(12px)}to{opacity:1;transform:translateY(0)}`;

/* ─── Translations ────────────────────────────────────────────────────────── */
const MDT = {
  back:            { fr:"← Marchés",                      en:"← All Markets",                  es:"← Mercados",                      de:"← Alle Märkte",                   pt:"← Mercados",                     ar:"← الأسواق"            },
  probHist:        { fr:"Historique des probabilités",     en:"Probability History",             es:"Historial de probabilidades",      de:"Wahrscheinlichkeitsverlauf",       pt:"Histórico de probabilidades",    ar:"تاريخ الاحتمالات"     },
  currProb:        { fr:"Probabilités actuelles",          en:"Current Probabilities",           es:"Probabilidades actuales",          de:"Aktuelle Wahrscheinlichkeiten",    pt:"Probabilidades atuais",          ar:"الاحتمالات الحالية"    },
  dailyVol:        { fr:"Volume journalier (14j)",         en:"Daily Volume (14 days)",          es:"Volumen diario (14 días)",         de:"Tägliches Volumen (14 Tage)",      pt:"Volume diário (14 dias)",        ar:"الحجم اليومي (14 يوماً)" },
  resolution:      { fr:"Critères de résolution",          en:"Resolution Criteria",             es:"Criterios de resolución",          de:"Auflösungskriterien",              pt:"Critérios de resolução",         ar:"معايير التسوية"        },
  trade:           { fr:"Trade",                           en:"Trade",                           es:"Operar",                           de:"Handeln",                          pt:"Negociar",                       ar:"تداول"                },
  openTrade:       { fr:"Ouvrir le panneau →",             en:"Open Trade Panel →",              es:"Abrir panel →",                    de:"Trade-Panel öffnen →",             pt:"Abrir painel →",                 ar:"فتح لوحة التداول →"    },
  marketStats:     { fr:"Statistiques",                    en:"Market Stats",                    es:"Estadísticas",                     de:"Marktstatistiken",                 pt:"Estatísticas",                   ar:"إحصائيات السوق"       },
  keyDates:        { fr:"Dates clés",                      en:"Key Dates",                       es:"Fechas clave",                     de:"Wichtige Daten",                   pt:"Datas importantes",              ar:"تواريخ رئيسية"         },
  created:         { fr:"Créé",                            en:"Created",                         es:"Creado",                           de:"Erstellt",                         pt:"Criado",                         ar:"تاريخ الإنشاء"         },
  closes:          { fr:"Ferme",                           en:"Closes",                          es:"Cierra",                           de:"Schließt",                         pt:"Fecha",                          ar:"يغلق"                 },
  resolves:        { fr:"Résolution",                      en:"Resolves",                        es:"Resolución",                       de:"Auflösung",                        pt:"Resolução",                      ar:"التسوية"              },
  resolved:        { fr:"Résolu",                          en:"Resolved",                        es:"Resuelto",                         de:"Aufgelöst",                        pt:"Resolvido",                      ar:"مُحسَّم"               },
  activity:        { fr:"Activité récente",                en:"Recent Activity",                 es:"Actividad reciente",               de:"Aktuelle Aktivität",               pt:"Atividade recente",              ar:"النشاط الأخير"         },
  volume:          { fr:"Volume",                          en:"Volume",                          es:"Volumen",                          de:"Volumen",                          pt:"Volume",                         ar:"الحجم"                },
  liquidity:       { fr:"Liquidité",                       en:"Liquidity",                       es:"Liquidez",                         de:"Liquidität",                       pt:"Liquidez",                       ar:"السيولة"              },
  shares:          { fr:"Parts",                           en:"Shares",                          es:"Partes",                           de:"Anteile",                          pt:"Partes",                         ar:"أسهم"                 },
  probability:     { fr:"probabilité",                     en:"probability",                     es:"probabilidad",                     de:"Wahrscheinlichkeit",               pt:"probabilidade",                  ar:"احتمالية"             },
  returnPer:       { fr:"Retour",                          en:"Return",                          es:"Retorno",                          de:"Rendite",                          pt:"Retorno",                        ar:"العائد"               },
  signIn:          { fr:"Connectez-vous pour trader",      en:"Sign in to place trades",         es:"Inicia sesión para operar",        de:"Melde dich an zum Handeln",        pt:"Faça login para negociar",       ar:"سجّل الدخول للتداول"   },
  noActivity:      { fr:"Aucune activité récente.",        en:"No recent activity.",             es:"Sin actividad reciente.",          de:"Keine aktuellen Aktivitäten.",     pt:"Nenhuma atividade recente.",     ar:"لا يوجد نشاط مؤخراً."  },
  bought:          { fr:"a acheté",                        en:"bought",                          es:"compró",                           de:"kaufte",                           pt:"comprou",                        ar:"اشترى"                },
  sold:            { fr:"a vendu",                         en:"sold",                            es:"vendió",                           de:"verkaufte",                        pt:"vendeu",                         ar:"باع"                  },
  marketResolved:  { fr:"Marché résolu",                   en:"Market Resolved",                 es:"Mercado resuelto",                 de:"Markt aufgelöst",                  pt:"Mercado resolvido",              ar:"السوق محسوم"           },
  marketClosed:    { fr:"Marché fermé",                    en:"Market Closed",                   es:"Mercado cerrado",                  de:"Markt geschlossen",                pt:"Mercado fechado",                ar:"السوق مغلق"            },
  winner:          { fr:"Gagnant",                         en:"Winner",                          es:"Ganador",                          de:"Gewinner",                         pt:"Vencedor",                       ar:"الفائز"               },
  noMoreTrades:    { fr:"Aucun trade accepté.",            en:"No more trades accepted.",        es:"No se aceptan más trades.",        de:"Keine Trades mehr.",               pt:"Sem mais trades.",               ar:"لا مزيد من الصفقات."   },
  loading:         { fr:"Chargement…",                     en:"Loading…",                        es:"Cargando…",                        de:"Laden…",                           pt:"Carregando…",                    ar:"جارٍ التحميل…"        },
  notFound:        { fr:"Marché introuvable.",              en:"Market not found.",               es:"Mercado no encontrado.",           de:"Markt nicht gefunden.",            pt:"Mercado não encontrado.",         ar:"السوق غير موجود."      },
  today:           { fr:"Aujourd'hui",                     en:"Today",                           es:"Hoy",                              de:"Heute",                            pt:"Hoje",                           ar:"اليوم"                },
  noVol:           { fr:"Aucun volume enregistré.",        en:"No volume recorded.",             es:"Sin volumen registrado.",          de:"Kein Volumen aufgezeichnet.",      pt:"Nenhum volume registrado.",      ar:"لا يوجد حجم مسجّل."    },
  live:            { fr:"● live",                          en:"● live",                          es:"● en vivo",                        de:"● live",                           pt:"● ao vivo",                      ar:"● مباشر"               },
  chartLine:       { fr:"Ligne",                           en:"Line",                            es:"Línea",                            de:"Linie",                            pt:"Linha",                          ar:"خط"                   },
  chartCandle:     { fr:"Chandeliers",                     en:"Candlestick",                     es:"Velas",                            de:"Kerzen",                           pt:"Velas",                          ar:"شموع"                 },
  noCandleData:    { fr:"Données insuffisantes.",           en:"Not enough data.",                es:"Datos insuficientes.",             de:"Zu wenige Daten.",                 pt:"Dados insuficientes.",           ar:"بيانات غير كافية."     },
  bullish:         { fr:"Haussier",                        en:"Bullish",                         es:"Alcista",                          de:"Bullisch",                         pt:"Alta",                           ar:"صاعد"                  },
  bearish:         { fr:"Baissier",                        en:"Bearish",                         es:"Bajista",                          de:"Bärisch",                          pt:"Baixa",                          ar:"هابط"                  },
  resolvedBadge:   { fr:"Résolu",                          en:"Resolved",                        es:"Resuelto",                         de:"Aufgelöst",                        pt:"Resolvido",                      ar:"محسوم"                 },
  winnerLabel:     { fr:"Gagnant",                         en:"Winner",                          es:"Ganador",                          de:"Gewinner",                         pt:"Vencedor",                       ar:"الفائز"               },
};
function mdt(key, lang) { return MDT[key]?.[lang] ?? MDT[key]?.fr ?? key; }

/* ─── Mock data ────────────────────────────────────────────────────────────── */
const MOCK_MARKETS = {
  1:{id:1,title:"Bitcoin dépassera-t-il 100K$ avant fin 2026 ?",description:"Ce marché se résout OUI si le prix du Bitcoin sur Coinbase atteint ou dépasse 100 000 USD à n'importe quel moment avant le 31 décembre 2026.",category_name:"Crypto",status:"OPEN",volume:"2430000",liquidity:"340000",image_url:"",created_at:"2026-01-15T10:00:00Z",close_date:"2026-12-31T23:59:00Z",resolution_date:"2026-01-02T00:00:00Z",outcomes:[{id:1,name:"Oui",probability:"0.72",shares_outstanding:"124000"},{id:2,name:"Non",probability:"0.28",shares_outstanding:"48000"}]},
  2:{id:2,title:"Élections US 2024 — Le candidat démocrate gagnera-t-il ?",description:"Résolution OUI si le candidat du Parti démocrate remporte l'élection présidentielle américaine de 2024.",category_name:"Politique",status:"RESOLVED",volume:"8200000",liquidity:"0",image_url:"",created_at:"2024-06-01T00:00:00Z",close_date:"2024-11-05T23:00:00Z",resolution_date:"2024-11-06T12:00:00Z",resolved_at:"2024-11-06T14:22:00Z",resolved_outcome:"Non",outcomes:[{id:3,name:"Oui",probability:"0.45",shares_outstanding:"820000"},{id:4,name:"Non",probability:"0.55",shares_outstanding:"920000"}]},
  3:{id:3,title:"L'IA sera-t-elle approuvée par la FDA avant 2026 ?",description:"Résolution OUI si la FDA approuve officiellement un système d'IA avant le 1er janvier 2026.",category_name:"Tech",status:"OPEN",volume:"560000",liquidity:"82000",image_url:"",created_at:"2026-02-01T00:00:00Z",close_date:"2026-05-31T23:59:00Z",resolution_date:"2026-06-01T12:00:00Z",outcomes:[{id:5,name:"Oui",probability:"0.61",shares_outstanding:"34200"},{id:6,name:"Non",probability:"0.39",shares_outstanding:"21800"}]},
  4:{id:4,title:"Champions League 2024/25 — Le Real Madrid gagnera-t-il ?",description:"Résolution OUI si le Real Madrid CF remporte la finale de la Ligue des Champions 2024/25.",category_name:"Sports",status:"OPEN",volume:"1100000",liquidity:"155000",image_url:"",created_at:"2024-09-01T00:00:00Z",close_date:"2026-05-31T20:00:00Z",resolution_date:"2026-05-31T22:00:00Z",outcomes:[{id:7,name:"Oui",probability:"0.34",shares_outstanding:"41000"},{id:8,name:"Non",probability:"0.66",shares_outstanding:"79000"}]},
  5:{id:5,title:"La Fed baissera-t-elle ses taux en Q1 2026 ?",description:"Résolution OUI si la Réserve fédérale annonce au moins une baisse de taux au cours du T1 2026.",category_name:"Finance",status:"CLOSED",volume:"3200000",liquidity:"0",image_url:"",created_at:"2024-12-01T00:00:00Z",close_date:"2026-03-31T23:00:00Z",resolution_date:"2026-04-01T12:00:00Z",outcomes:[{id:9,name:"Oui",probability:"0.58",shares_outstanding:"186000"},{id:10,name:"Non",probability:"0.42",shares_outstanding:"134000"}]},
  6:{id:6,title:"Ethereum aura-t-il plus de 100M de validateurs en 2026 ?",description:"Résolution OUI si Ethereum dépasse 100 millions de validateurs actifs avant fin 2026.",category_name:"Crypto",status:"OPEN",volume:"890000",liquidity:"112000",image_url:"",created_at:"2026-01-20T00:00:00Z",close_date:"2026-03-31T23:59:00Z",resolution_date:"2026-04-01T12:00:00Z",outcomes:[{id:11,name:"Oui",probability:"0.81",shares_outstanding:"72000"},{id:12,name:"Non",probability:"0.19",shares_outstanding:"17000"}]},
};

function _fallbackHistory(outcomes, days=30) {
  const now=Date.now(); const base=outcomes.map(o=>parseFloat(o.probability)||0.5);
  return Array.from({length:days+1},(_,i)=>{
    const noise=Math.sin(i*2.3+1)*.06+Math.sin(i*.7)*.04;
    const p0=Math.max(.05,Math.min(.95,(base[0]||.5)+noise*(i/days)));
    return{t:now-(days-i)*86400000,date:new Date(now-(days-i)*86400000),values:base.length>1?[p0,1-p0]:[p0]};
  });
}

/* ─── Parse API history avec coupure par date string (pas getTime) ─────── */
function _parseApiHistory(data, cutoffDate) {
  const points = data.map(p => ({
    t:      new Date(p.date).getTime(),
    date:   new Date(p.date),
    values: p.values,
  }));
  if (!cutoffDate) return points;
  const cutoffStr = new Date(cutoffDate).toISOString().slice(0, 10);
  return points.filter(p => p.date.toISOString().slice(0, 10) <= cutoffStr);
}

function _parseApiActivity(trades) {
  return trades.map(t => {
    const ms = Date.now() - new Date(t.created_at).getTime();
    const min = Math.floor(ms / 60000);
    const time = min < 60 ? `${min}m` : min < 1440 ? `${Math.floor(min/60)}h` : `${Math.floor(min/1440)}d`;
    return { user: t.user, buy: t.side === "BUY", outcome: t.outcome, amount: fmtAmount(t.total_usdc), time };
  });
}

function fmtAmount(value) {
  const n = parseFloat(value || 0);
  if (n === 0)     return "0";
  if (n >= 1000)   return `${(n/1000).toFixed(1)}K`;
  if (n >= 100)    return n.toFixed(0);
  if (n >= 10)     return n.toFixed(1);
  if (n >= 1)      return n.toFixed(2);
  if (n >= 0.01)   return n.toFixed(3);
  if (n >= 0.0001) return n.toFixed(5);
  return n.toExponential(2);
}

const COLORS_BASE = ["#00e676","#ff3b5c","#4fc3f7","#ffc107","#c084fc","#fb923c","#34d399","#f472b6","#60a5fa","#a78bfa"];
const COLORS = new Proxy(COLORS_BASE, {
  get(target, prop) {
    const i = parseInt(prop);
    if (!isNaN(i) && i >= 0) {
      if (i < target.length) return target[i];
      const hue = (i * 37 + 160) % 360;
      return `hsl(${hue},70%,58%)`;
    }
    return target[prop];
  }
});
const EMOJI_MAP = {Politics:"🏛️",Politique:"🏛️",Sports:"⚽",Crypto:"₿",Finance:"📈",Tech:"💻",Science:"🔬",Entertainment:"🎬",Other:"🌐"};

/* Locale map pour toLocaleDateString */
const LOCALE_MAP = { fr:"fr-FR", en:"en-GB", es:"es-ES", de:"de-DE", pt:"pt-BR", ar:"ar-SA" };
function fmtDate(d, lang="fr") {
  if(!d) return "—";
  const locale = LOCALE_MAP[lang] || "fr-FR";
  return new Date(d).toLocaleDateString(locale, {month:"short", day:"numeric", year:"numeric"});
}
function fmtVol(v)  { const n=parseFloat(v||0); return n>=1e6?`${(n/1e6).toFixed(2)}M EVP`:n>=1e3?`${(n/1e3).toFixed(1)}K EVP`:`${n.toFixed(0)} EVP`; }
function fmtShares(s){ const n=parseFloat(s||0); return n>=1000?`${(n/1000).toFixed(1)}K`:n.toFixed(0); }

/* Filtre les barres de volume selon la coupure (comparaison string YYYY-MM-DD) */
function _filterBars(bars, cutoffDate) {
  if (!cutoffDate) return bars;
  const cutoffStr = new Date(cutoffDate).toISOString().slice(0, 10);
  return bars.filter(b => b.date <= cutoffStr);
}

/* ─── Styled components ───────────────────────────────────────────────────── */
const Wrapper      = styled.div`
  max-width:1100px;margin:0 auto;padding:2rem 2rem 4rem;
  @media(max-width:720px){ padding-bottom: 5.5rem; }
`;
const BackBtn      = styled.button`background:none;border:none;color:${({theme})=>theme.textMuted};font-size:.8rem;cursor:pointer;margin-bottom:1.5rem;display:flex;align-items:center;gap:.4rem;padding:0;transition:color .15s;&:hover{color:${({theme})=>theme.text};}`;
const Hero         = styled.div`display:flex;gap:1.25rem;align-items:flex-start;margin-bottom:2rem;animation:${fadeUp} .3s ease;`;
const Thumb        = styled.div`width:72px;height:72px;border-radius:14px;background:${({theme})=>theme.bgCard};border:1px solid ${({theme})=>theme.border};flex-shrink:0;font-size:2.2rem;display:flex;align-items:center;justify-content:center;`;
const HeroInfo     = styled.div`flex:1;min-width:0;`;
const HeroCat      = styled.div`font-family:${({theme})=>theme.fontMono};font-size:.62rem;letter-spacing:.08em;text-transform:uppercase;color:${({theme})=>theme.textMuted};margin-bottom:.3rem;`;
const HeroTitle    = styled.h1`font-family:${({theme})=>theme.fontDisplay};font-size:clamp(1.1rem,2.5vw,1.6rem);font-weight:700;line-height:1.2;letter-spacing:-.025em;margin-bottom:.5rem;`;
const BadgeRow     = styled.div`display:flex;gap:.4rem;flex-wrap:wrap;align-items:center;`;
const Badge        = styled.span`font-family:${({theme})=>theme.fontMono};font-size:.6rem;padding:.2rem .55rem;border-radius:4px;letter-spacing:.05em;background:${({$g,$r,$b,theme})=>$g?theme.greenDim:$r?theme.redDim:$b?"rgba(255,193,7,.1)":theme.bgHover};color:${({$g,$r,$b,theme})=>$g?theme.green:$r?theme.red:$b?theme.gold:theme.textDim};border:1px solid ${({$g,$r,$b,theme})=>$g?theme.green+"44":$r?theme.red+"44":$b?theme.gold+"44":theme.border};`;
const Layout       = styled.div`display:grid;grid-template-columns:1fr 300px;gap:1.25rem;@media(max-width:800px){grid-template-columns:1fr;}`;
const Left         = styled.div`display:flex;flex-direction:column;gap:1.25rem;`;
const Right        = styled.div`display:flex;flex-direction:column;gap:1.25rem;`;
const Panel        = styled.div`background:${({theme})=>theme.bgCard};border:1px solid ${({theme})=>theme.border};border-radius:12px;padding:1.25rem;animation:${fadeUp} .4s ease both;animation-delay:${({$d})=>$d||0}s;`;
const PanelHead    = styled.div`display:flex;align-items:center;justify-content:space-between;margin-bottom:1rem;`;
const PanelTitle   = styled.h3`font-family:${({theme})=>theme.fontMono};font-size:.65rem;text-transform:uppercase;letter-spacing:.08em;color:${({theme})=>theme.textMuted};`;
const OutcomeList  = styled.div`display:flex;flex-direction:column;gap:.85rem;`;
const OutcomeRow   = styled.div`display:flex;flex-direction:column;gap:.3rem;`;
const OutcomeTop   = styled.div`display:flex;align-items:center;justify-content:space-between;`;
const OutcomeName  = styled.span`font-size:.85rem;font-weight:600;`;
const OutcomePct   = styled.span`font-family:${({theme})=>theme.fontMono};font-size:1rem;font-weight:700;color:${({$col})=>$col};`;
const BarTrack     = styled.div`height:8px;background:${({theme})=>theme.border};border-radius:5px;overflow:hidden;`;
const BarFill      = styled.div`height:100%;border-radius:5px;transition:width .8s cubic-bezier(.4,0,.2,1);`;
const OutcomeMeta  = styled.div`display:flex;gap:.8rem;font-family:${({theme})=>theme.fontMono};font-size:.65rem;color:${({theme})=>theme.textMuted};`;
const ChartWrap    = styled.div`position:relative;width:100%;`;
const ChartSvg     = styled.svg`width:100%;display:block;overflow:visible;`;
const ChartLegend  = styled.div`display:flex;gap:1rem;margin-top:.75rem;flex-wrap:wrap;`;
const LegItem      = styled.div`display:flex;align-items:center;gap:.35rem;font-family:${({theme})=>theme.fontMono};font-size:.65rem;color:${({theme})=>theme.textDim};`;
const LegDot       = styled.span`width:8px;height:8px;border-radius:50%;background:${({$c})=>$c};display:inline-block;`;
const RangeTabs    = styled.div`display:flex;gap:.3rem;`;
const RangeBtn     = styled.button`font-family:${({theme})=>theme.fontMono};font-size:.6rem;padding:.2rem .5rem;border-radius:4px;letter-spacing:.05em;cursor:pointer;transition:all .15s;border:1px solid ${({$active,theme})=>$active?theme.borderLight:theme.border};background:${({$active,theme})=>$active?theme.bgHover:"transparent"};color:${({$active,theme})=>$active?theme.text:theme.textMuted};`;
const TradePanel   = styled(Panel)`border-color:${({theme})=>theme.green}33;`;
const TradeBtnBig  = styled.button`width:100%;padding:.85rem;border-radius:8px;border:none;background:${({theme})=>theme.green};color:#000;font-family:${({theme})=>theme.fontBody};font-size:.9rem;font-weight:700;letter-spacing:.04em;cursor:pointer;transition:opacity .15s;&:hover{opacity:.9;}&:disabled{opacity:.35;cursor:not-allowed;background:${({theme})=>theme.bgHover};color:${({theme})=>theme.textMuted};}`;
const QuickBuy     = styled.div`display:grid;grid-template-columns:1fr 1fr;gap:.4rem;margin-bottom:.6rem;`;
const QuickBtn     = styled.button`padding:.5rem;border-radius:6px;font-size:.72rem;font-weight:600;cursor:pointer;font-family:${({theme})=>theme.fontMono};transition:all .15s;border:1px solid ${({$green,theme})=>$green?theme.green+"44":theme.red+"44"};background:${({$green,theme})=>$green?theme.greenDim:theme.redDim};color:${({$green,theme})=>$green?theme.green:theme.red};&:hover{opacity:.75;}`;
const StatGrid     = styled.div`display:grid;grid-template-columns:1fr 1fr;gap:.6rem;`;
const StatBox      = styled.div`background:${({theme})=>theme.bg};border:1px solid ${({theme})=>theme.border};border-radius:8px;padding:.75rem;`;
const StatVal      = styled.div`font-family:${({theme})=>theme.fontMono};font-size:.95rem;font-weight:500;color:${({theme})=>theme.text};`;
const StatLbl      = styled.div`font-size:.62rem;text-transform:uppercase;letter-spacing:.07em;color:${({theme})=>theme.textMuted};margin-top:.2rem;`;
const DateList     = styled.div`display:flex;flex-direction:column;gap:.45rem;`;
const DateRow      = styled.div`display:flex;justify-content:space-between;align-items:center;font-size:.75rem;`;
const DateKey      = styled.span`color:${({theme})=>theme.textMuted};`;
const DateVal      = styled.span`font-family:${({theme})=>theme.fontMono};color:${({theme})=>theme.textDim};`;
const ActivityList = styled.div`display:flex;flex-direction:column;max-height:280px;overflow-y:auto;scrollbar-width:thin;scrollbar-color:${({theme})=>theme.border} transparent;&::-webkit-scrollbar{width:4px;}&::-webkit-scrollbar-thumb{background:${({theme})=>theme.border};border-radius:2px;}`;
const ActivityRow  = styled.div`display:flex;align-items:center;gap:.6rem;padding:.55rem 0;border-bottom:1px solid ${({theme})=>theme.border};&:last-child{border-bottom:none;}`;
const ActivityDot  = styled.span`width:6px;height:6px;border-radius:50%;flex-shrink:0;background:${({$buy,theme})=>$buy?theme.green:theme.red};`;
const ActivityText = styled.span`font-size:.72rem;color:${({theme})=>theme.textDim};flex:1;`;
const ActivityAmt  = styled.span`font-family:${({theme})=>theme.fontMono};font-size:.72rem;color:${({$buy,theme})=>$buy?theme.green:theme.red};`;
const ActivityTime = styled.span`font-family:${({theme})=>theme.fontMono};font-size:.62rem;color:${({theme})=>theme.textMuted};`;
const Desc         = styled.p`font-size:.8rem;line-height:1.65;color:${({theme})=>theme.textDim};white-space:pre-wrap;`;
const FullPanel    = styled(Panel)`margin-top:1.25rem;`;
const LiveBadge    = styled.span`font-family:${({theme})=>theme.fontMono};font-size:.58rem;color:${({theme})=>theme.green};margin-left:.5rem;`;

/* ─── Chart fullscreen modal ─────────────────────────────────────────────── */
const ExpandBtn = styled.button`
  background:none;border:1px solid ${({theme})=>theme.border};border-radius:6px;
  color:${({theme})=>theme.textMuted};cursor:pointer;padding:.18rem .45rem;
  font-size:.8rem;line-height:1;transition:all .15s;margin-left:.3rem;
  &:hover{border-color:${({theme})=>theme.borderLight};color:${({theme})=>theme.text};}
`;
const ChartModalOverlay = styled.div`
  position:fixed;inset:0;z-index:500;background:rgba(0,0,0,.82);
  backdrop-filter:blur(6px);display:flex;align-items:center;justify-content:center;
  padding:24px;animation:cmFadeIn .18s ease;
  @keyframes cmFadeIn{from{opacity:0}to{opacity:1}}
`;
const ChartModalBox = styled.div`
  background:${({theme})=>theme.bgCard};border:1px solid ${({theme})=>theme.borderLight};
  border-radius:16px;width:100%;max-width:1100px;max-height:90vh;
  display:flex;flex-direction:column;overflow:hidden;
  animation:cmSlide .2s cubic-bezier(.16,1,.3,1);
  @keyframes cmSlide{from{opacity:0;transform:scale(.97)}to{opacity:1;transform:scale(1)}}
`;
const ChartModalHead = styled.div`
  display:flex;align-items:center;justify-content:space-between;
  padding:1rem 1.25rem .75rem;border-bottom:1px solid ${({theme})=>theme.border};
  flex-shrink:0;flex-wrap:wrap;gap:.5rem;
`;
const ChartModalTitle = styled.div`
  font-family:${({theme})=>theme.fontMono};font-size:.7rem;text-transform:uppercase;
  letter-spacing:.08em;color:${({theme})=>theme.textMuted};
  display:flex;align-items:center;gap:.5rem;
`;
const ChartModalClose = styled.button`
  background:none;border:1px solid ${({theme})=>theme.border};border-radius:8px;
  color:${({theme})=>theme.textMuted};cursor:pointer;padding:.35rem .7rem;
  font-size:.8rem;transition:all .15s;
  &:hover{border-color:${({theme})=>theme.borderLight};color:${({theme})=>theme.text};}
`;
const ChartModalBody = styled.div`
  flex:1;overflow-y:auto;padding:1.25rem 1.5rem 1.5rem;
  svg{height:340px !important;}
  @media(max-width:600px){padding:.75rem;svg{height:220px !important;}}
`;

const POLL_INTERVAL = 12000;

/* ─── Candlestick builder ─────────────────────────────────────────────────── */
function buildCandles(history, outcomeIdx = 0, groupSize = 2) {
  const candles = [];
  const allVals = history.map(p => p.values[outcomeIdx] ?? 0);
  const mean    = allVals.reduce((s,v)=>s+v,0)/allVals.length;
  const stdDev  = Math.sqrt(allVals.reduce((s,v)=>s+(v-mean)**2,0)/allVals.length)||0.01;
  function seededRand(seed){const x=Math.sin(seed+1)*43758.5453123;return x-Math.floor(x);}
  for(let i=0;i<history.length;i+=groupSize){
    const chunk=history.slice(i,i+groupSize);if(!chunk.length)continue;
    const vals=chunk.map(p=>p.values[outcomeIdx]??0);
    const open=vals[0],close=vals[vals.length-1];
    const chunkMax=Math.max(...vals),chunkMin=Math.min(...vals);
    const wickScale=stdDev*1.2;
    const wickHi=seededRand(i*7+1)*wickScale*2.5;
    const wickLo=seededRand(i*7+3)*wickScale*2.5;
    const prevClose=candles.length>0?candles[candles.length-1].close:null;
    let bull;
    if(close!==open)bull=close>open;
    else if(prevClose!==null)bull=close>=prevClose;
    else bull=true;
    candles.push({date:chunk[0].date,open,close,bull,
      high:Math.min(0.99,chunkMax+wickHi),low:Math.max(0.01,chunkMin-wickLo),t:chunk[0].t});
  }
  return candles;
}

const ChartToggleRow = styled.div`display:flex;gap:.25rem;`;
const ChartToggleBtn = styled.button`
  font-family:${({theme})=>theme.fontMono};font-size:.58rem;padding:.2rem .55rem;
  border-radius:4px;letter-spacing:.05em;cursor:pointer;transition:all .15s;
  border:1px solid ${({$active,theme})=>$active?theme.borderLight:theme.border};
  background:${({$active,theme})=>$active?theme.bgHover:"transparent"};
  color:${({$active,theme})=>$active?theme.text:theme.textMuted};
`;

/* ─── Candlestick Chart ───────────────────────────────────────────────────── */
function CandlestickChart({history,outcomes,lang="fr"}){
  const locale=LOCALE_MAP[lang]||"fr-FR";
  const [hovIdx,setHovIdx]=React.useState(null);
  const [activeOutcome,setActiveOutcome]=React.useState(0);
  const W=520,H=200,PAD={t:10,r:8,b:28,l:40};
  const iW=W-PAD.l-PAD.r,iH=H-PAD.t-PAD.b;
  const TARGET_CANDLES=28;
  const groupSize=Math.max(1,Math.round(history.length/TARGET_CANDLES));
  const candles=buildCandles(history,activeOutcome,groupSize);
  const N=candles.length;
  if(N<3)return(<div style={{height:H,display:"flex",alignItems:"center",justifyContent:"center",color:"#5a6a7a",fontSize:".75rem",fontFamily:"DM Mono,monospace"}}>{mdt("noCandleData",lang)}</div>);
  const bandW=iW/N,bodyW=Math.max(2,Math.min(12,bandW*.8));
  const xC=i=>PAD.l+(i+.5)*bandW;
  const allVals=candles.flatMap(c=>[c.high,c.low]);
  const minV=Math.min(...allVals),maxV=Math.max(...allVals);
  const pad5=(maxV-minV)*.05||.005,lo=minV-pad5,hi=maxV+pad5,vRange=hi-lo;
  const yS=v=>PAD.t+(1-(v-lo)/vRange)*iH;
  const yTicks=[0,1,2,3,4].map(i=>lo+(vRange/4)*i);
  const xStep=Math.max(1,Math.floor(N/5));
  const bullColor="#00e676",bearColor="#ff3b5c";
  const bullFill="rgba(0,230,118,0.18)",bearFill="rgba(255,59,92,0.15)";
  const hov=hovIdx!==null?candles[hovIdx]:null;
  const handleMouseMove=e=>{
    const rect=e.currentTarget.getBoundingClientRect();
    const svgX=((e.clientX-rect.left)/rect.width)*W-PAD.l;
    setHovIdx(Math.max(0,Math.min(N-1,Math.floor(svgX/bandW))));
  };
  return(
    <ChartWrap>
      {outcomes.length>1&&(
        <ChartToggleRow style={{marginBottom:".5rem"}}>
          {outcomes.map((o,i)=>(
            <ChartToggleBtn key={o.id} $active={activeOutcome===i} onClick={()=>setActiveOutcome(i)}
              style={{borderColor:activeOutcome===i?COLORS[i]+"88":undefined,color:activeOutcome===i?COLORS[i]:undefined}}>
              {o.name}
            </ChartToggleBtn>
          ))}
        </ChartToggleRow>
      )}
      <ChartSvg viewBox={`0 0 ${W} ${H}`} style={{cursor:"crosshair"}} onMouseLeave={()=>setHovIdx(null)} onMouseMove={handleMouseMove}>
        {yTicks.map((v,ti)=>(<g key={ti}><line x1={PAD.l} x2={W-PAD.r} y1={yS(v)} y2={yS(v)} stroke="#1e2530" strokeWidth="1" strokeDasharray={ti===2?"3 3":"none"}/><text x={PAD.l-4} y={yS(v)+4} textAnchor="end" fill="#5a6a7a" fontSize="9" fontFamily="DM Mono,monospace">{(v*100).toFixed(0)}</text></g>))}
        {candles.map((c,i)=>i%xStep===0&&(<text key={i} x={xC(i)} y={H-4} textAnchor="middle" fill="#5a6a7a" fontSize="9" fontFamily="DM Mono,monospace">{new Date(c.date).toLocaleDateString(locale,{month:"short",day:"numeric"})}</text>))}
        {candles.map((c,i)=>{
          const x=xC(i),bull=c.bull,col=bull?bullColor:bearColor,fill=bull?bullFill:bearFill;
          const yO=yS(c.open),yC2=yS(c.close),yH=yS(c.high),yL=yS(c.low);
          const bodyTop=Math.min(yO,yC2),bodyH=Math.max(1.5,Math.abs(yC2-yO));
          const isHov=hovIdx===i;
          return(<g key={i} opacity={isHov?1:hovIdx!==null?.55:.9}>
            <line x1={x} x2={x} y1={yH} y2={bodyTop} stroke={col} strokeWidth={isHov?1.5:1}/>
            <line x1={x} x2={x} y1={bodyTop+bodyH} y2={yL} stroke={col} strokeWidth={isHov?1.5:1}/>
            <rect x={x-bodyW/2} y={bodyTop} width={bodyW} height={bodyH} rx="1.5" fill={fill} stroke={col} strokeWidth={isHov?1.8:1.2}/>
            {bodyH>4&&(<line x1={x} x2={x} y1={bodyTop+2} y2={bodyTop+bodyH-2} stroke={col} strokeWidth="1" strokeOpacity={isHov?.55:.35} strokeDasharray="1.5 2"/>)}
          </g>);
        })}
        {hovIdx!==null&&(<line x1={xC(hovIdx)} x2={xC(hovIdx)} y1={PAD.t} y2={H-PAD.b} stroke="#2a3340" strokeWidth="1" strokeDasharray="3 3"/>)}
        {hov&&(()=>{
          const tx=Math.min(Math.max(xC(hovIdx),68),W-68),col=hov.bull?bullColor:bearColor;
          return(<g>
            <rect x={tx-62} y={PAD.t} width={124} height={76} rx="5" fill="#0e1115" stroke="#2a3340" strokeWidth="1"/>
            <text x={tx} y={PAD.t+12} textAnchor="middle" fill="#5a6a7a" fontSize="8" fontFamily="DM Mono,monospace">{new Date(hov.date).toLocaleDateString(locale,{day:"2-digit",month:"short"})}</text>
            {[["O",hov.open],["H",hov.high],["L",hov.low],["C",hov.close]].map(([label,val],li)=>(
              <g key={label}>
                <text x={tx-48} y={PAD.t+24+li*13} fill="#5a6a7a" fontSize="9" fontFamily="DM Mono,monospace">{label}</text>
                <text x={tx+48} y={PAD.t+24+li*13} textAnchor="end" fill={label==="C"?col:"#e8edf2"} fontSize="9" fontFamily="DM Mono,monospace" fontWeight={label==="C"?"700":"400"}>{(val*100).toFixed(1)}%</text>
              </g>
            ))}
          </g>);
        })()}
      </ChartSvg>
      <ChartLegend>
        {outcomes.map((o,i)=>{
          const lastPoint=history[history.length-1];
          const lastVal=lastPoint?.values[i]??parseFloat(o.probability)??0;
          return(<LegItem key={o.id} style={{opacity:activeOutcome===i?1:.4}}>
            <LegDot $c={COLORS[i]}/>{o.name} — <span style={{color:COLORS[i],fontWeight:600}}>{(lastVal*100).toFixed(1)}%</span>
          </LegItem>);
        })}
        <LegItem style={{marginLeft:"auto",gap:".5rem"}}>
          <LegDot $c="#00e676" style={{borderRadius:2}}/> {mdt("bullish",lang)}
          <LegDot $c="#ff3b5c" style={{borderRadius:2}}/> {mdt("bearish",lang)}
        </LegItem>
      </ChartLegend>
    </ChartWrap>
  );
}

/* ─── Line Chart ──────────────────────────────────────────────────────────── */
function LineChart({history,outcomes,lang="fr"}){
  const locale=LOCALE_MAP[lang]||"fr-FR";
  const [hover,setHover]=React.useState(null);
  const W=520,H=160,PAD={t:8,r:8,b:28,l:36};
  const iW=W-PAD.l-PAD.r,iH=H-PAD.t-PAD.b;
  if(!history.length)return null;
  const xS=i=>PAD.l+(i/(history.length-1))*iW;
  const yS=v=>PAD.t+(1-v)*iH;
  const paths=outcomes.map((_,oi)=>history.map((p,i)=>`${xS(i)},${yS(p.values[oi]??0)}`).join(" L ")).map(pts=>`M ${pts}`);
  const yTicks=[0,.25,.5,.75,1];
  const xTicks=[0,Math.floor(history.length/4),Math.floor(history.length/2),Math.floor(history.length*3/4),history.length-1];
  const hovered=hover!==null?history[hover.idx]:null;
  const hitW=iW/Math.max(history.length-1,1);
  return(
    <ChartWrap>
      <ChartSvg viewBox={`0 0 ${W} ${H}`} style={{cursor:hover!==null?"crosshair":"default"}}
        onMouseLeave={()=>setHover(null)}
        onMouseMove={e=>{const rect=e.currentTarget.getBoundingClientRect();const svgX=((e.clientX-rect.left)/rect.width)*W;const relX=svgX-PAD.l;const idx=Math.max(0,Math.min(history.length-1,Math.round(relX/hitW)));setHover({idx,x:xS(idx)});}}>
        {yTicks.map(v=>(<g key={v}><line x1={PAD.l} x2={W-PAD.r} y1={yS(v)} y2={yS(v)} stroke="#1e2530" strokeWidth="1" strokeDasharray={v===.5?"3 3":"none"}/><text x={PAD.l-4} y={yS(v)+4} textAnchor="end" fill="#5a6a7a" fontSize="9" fontFamily="DM Mono,monospace">{(v*100).toFixed(0)}</text></g>))}
        {xTicks.map(i=>history[i]&&(<text key={i} x={xS(i)} y={H-4} textAnchor="middle" fill="#5a6a7a" fontSize="9" fontFamily="DM Mono,monospace">{new Date(history[i].date).toLocaleDateString(locale,{month:"short",day:"numeric"})}</text>))}
        {paths.map((d,oi)=>(<path key={`a${oi}`} d={`${d} L ${xS(history.length-1)},${yS(0)} L ${xS(0)},${yS(0)} Z`} fill={COLORS[oi]} fillOpacity=".07"/>))}
        {paths.map((d,oi)=>(<path key={`l${oi}`} d={d} fill="none" stroke={COLORS[oi]} strokeWidth={hover!==null&&oi===0?"2.5":oi===0?"2":"1.5"} strokeLinejoin="round" strokeLinecap="round"/>))}
        {hover!==null&&<line x1={hover.x} x2={hover.x} y1={PAD.t} y2={H-PAD.b} stroke="#2a3340" strokeWidth="1" strokeDasharray="3 3"/>}
        {outcomes.map((_,oi)=>{const pt=hovered||history[history.length-1];const idx=hovered?hover.idx:history.length-1;return(<circle key={`d${oi}`} cx={xS(idx)} cy={yS(pt?.values[oi]??0)} r={hover!==null?4:3} fill={COLORS[oi]} stroke="#080a0c" strokeWidth="1.5"/>);})}
        {hovered&&(()=>{
          // Largeur dynamique : un nom court ("Oui"/"Non") n'a pas besoin de la
          // même place qu'un nom long ("Erling Haaland", "Vinícius Júnior") —
          // une boîte figée à 116px les faisait se chevaucher avec le pourcentage.
          const maxNameLen = Math.max(...outcomes.map(o => (o.name || "").length), 3);
          const nameColW = Math.max(60, maxNameLen * 5.6); // ~5.6px/caractère en DM Mono 9px
          const valColW  = 40; // assez pour "100.0%"
          const boxW = Math.min(nameColW + valColW + 24, W - 16);
          const boxH = 18 + outcomes.length * 16;
          const tx = Math.min(Math.max(hover.x, boxW / 2 + 2), W - boxW / 2 - 2);
          const boxLeft  = tx - boxW / 2;
          const nameX = boxLeft + 18;
          const valX  = tx + boxW / 2 - 10;
          return(<g>
            <rect x={boxLeft} y={PAD.t} width={boxW} height={boxH} rx="5" fill="#0e1115" stroke="#2a3340" strokeWidth="1"/>
            <text x={tx} y={PAD.t+12} textAnchor="middle" fill="#5a6a7a" fontSize="8" fontFamily="DM Mono,monospace">{new Date(hovered.date).toLocaleDateString(locale,{day:"2-digit",month:"short",year:"numeric"})}</text>
            {outcomes.map((o,oi)=>(<g key={oi}>
              <circle cx={boxLeft+10} cy={PAD.t+22+oi*16} r="3" fill={COLORS[oi]}/>
              <text x={nameX} y={PAD.t+26+oi*16} fill="#e8edf2" fontSize="9" fontFamily="DM Mono,monospace">{o.name}</text>
              <text x={valX} y={PAD.t+26+oi*16} textAnchor="end" fill={COLORS[oi]} fontSize="9" fontFamily="DM Mono,monospace" fontWeight="600">{((hovered.values[oi]??0)*100).toFixed(1)}%</text>
            </g>))}
          </g>);
        })()}
      </ChartSvg>
      <ChartLegend>
        {outcomes.map((o,i)=>{const lv=hovered?(hovered.values[i]??0):(history[history.length-1]?.values[i]??0);return(<LegItem key={o.id}><LegDot $c={COLORS[i]}/>{o.name} — <span style={{color:COLORS[i],fontWeight:600}}>{(lv*100).toFixed(1)}%</span></LegItem>);})}
      </ChartLegend>
    </ChartWrap>
  );
}

/* ─── Volume Chart ────────────────────────────────────────────────────────── */
function VolumeChart({bars,loading,lang,cutoffDate}){
  const W=520,H=80;
  const maxV=Math.max(...bars.map(b=>b.volume),1);
  const barW=W/bars.length-2;
  if(loading)return<div style={{height:H,background:"#141820",borderRadius:8}}/>;
  if(bars.every(b=>b.volume===0))return<div style={{height:H,display:"flex",alignItems:"center",justifyContent:"center",color:"#5a6a7a",fontSize:".75rem",fontFamily:"DM Mono,monospace"}}>{mdt("noVol",lang)}</div>;
  /* Label fin : date de la dernière barre (cohérent avec ce qui est affiché) */
  const lastBar=bars[bars.length-1];
  const lastLabel=cutoffDate&&lastBar
    ? new Date(lastBar.date).toLocaleDateString(LOCALE_MAP[lang]||"fr-FR",{day:"numeric",month:"short",year:"numeric"})
    : mdt("today",lang);
  const lastColor=cutoffDate?"#ffc107":"#5a6a7a";
  return(
    <ChartWrap>
      <ChartSvg viewBox={`0 0 ${W} ${H}`}>
        {bars.map((b,i)=>{const bH=Math.max(2,(b.volume/maxV)*(H-16));return(<rect key={i} x={i*(W/bars.length)+1} y={H-bH-8} width={barW} height={bH} rx="2" fill="#00e676" fillOpacity={i===bars.length-1?.85:b.volume>0?.35:.1}><title>{b.date} — {fmtVol(b.volume)}{b.trade_count?` · ${b.trade_count} trades`:""}</title></rect>);})}
        <text x={0} y={H} fill="#5a6a7a" fontSize="9" fontFamily="DM Mono,monospace">{bars[0]?.date}</text>
        <text x={W} y={H} textAnchor="end" fill={lastColor} fontSize="9" fontFamily="DM Mono,monospace">{lastLabel}</text>
      </ChartSvg>
    </ChartWrap>
  );
}

/* ─── Main ────────────────────────────────────────────────────────────────── */
export default function MarketDetail({marketId,user,onBack,initialMarket,onLoginClick,autoTrade,onAutoTradeConsumed}){
  const {lang}=useI18n();
  const [market,      setMarket]      = useState(initialMarket||null);
  const [loading,     setLoading]     = useState(!initialMarket);
  const [trading,     setTrading]     = useState(false);
  const [autoTradeInit,setAutoTradeInit]=useState(null); // {side,outcomeId} consommé une seule fois
  const [range,       setRange]       = useState(30);
  const [history,     setHistory]     = useState([]);
  const [activity,    setActivity]    = useState([]);
  const [volumeBars,  setVolumeBars]  = useState([]);
  const [histLoading, setHistLoading] = useState(true);
  const [actLoading,  setActLoading]  = useState(true);
  const [volLoading,  setVolLoading]  = useState(true);
  const [lastUpdate,  setLastUpdate]  = useState(null);
  const [chartType,   setChartType]   = useState("line");
  const [chartExpanded,setChartExpanded]=useState(false);
  const [imageExpanded,setImageExpanded]=useState(false);
  const mid=market?.id??marketId;
  const commentsRef=useRef(null);

  /* ── Date de coupure ──────────────────────────────────────────────────────
     Priorité : resolved_at > close_date > dernière date historique > null
     Ne s'applique QUE si statut !== OPEN                                   */
  const getCutoff=(m, hist=null)=>{
    if(!m||m.status==="OPEN") return null;
    if(m.resolved_at)  return m.resolved_at;
    if(m.close_date)   return m.close_date;
    /* Fallback : dernière date de l'historique déjà chargé */
    if(hist&&hist.length>0) return hist[hist.length-1].date.toISOString();
    return null;
  };

  const cutoff=getCutoff(market,history);

  useEffect(()=>{
    if(initialMarket){setMarket(initialMarket);return;}
    setLoading(true);
    fetchMarket(marketId).then(r=>setMarket(r.data)).catch(()=>{const m=MOCK_MARKETS[marketId];if(m)setMarket(m);}).finally(()=>setLoading(false));
  },[marketId,initialMarket]);

  /* Ouverture directe de l'écran de vente depuis "Mes positions" :
     dès que le marché est prêt, on ouvre le TradeModal pré-rempli
     (side + outcome), une seule fois par navigation entrante. */
  useEffect(()=>{
    if(autoTrade&&market&&!autoTradeInit){
      setAutoTradeInit({side:autoTrade.side||"SELL",outcomeId:autoTrade.outcomeId??null});
      setTrading(true);
      onAutoTradeConsumed?.();
    }
  },[autoTrade,market,autoTradeInit,onAutoTradeConsumed]);

  const refreshMarket  =useCallback(async()=>{if(!mid)return;try{const r=await fetchMarket(mid);setMarket(r.data);}catch{}},[mid]);
  const refreshHistory =useCallback(async()=>{
    if(!mid||!market)return;
    try{
      const co=getCutoff(market,null);
      const endDate=co?new Date(co).toISOString().slice(0,10):null;
      const r=await fetchMarketHistory(mid,range,endDate);
      const parsed=_parseApiHistory(r.data,null);
      const coWithHist=getCutoff(market,parsed);
      setHistory(coWithHist?_parseApiHistory(r.data,coWithHist):parsed);
    }catch{}
  },[mid,range,market?.status,market?.resolved_at,market?.close_date]);
  const refreshActivity=useCallback(async()=>{if(!mid)return;try{const r=await fetchMarketTrades(mid);const p=_parseApiActivity(r.data);setActivity(prev=>p.length>0?p:prev);}catch{}},[mid]);
  const refreshVolume  =useCallback(async()=>{
    if(!mid||!market)return;
    try{
      const co=getCutoff(market,history.length>0?history:null);
      const endDate=co?new Date(co).toISOString().slice(0,10):null;
      const r=await fetchMarketVolume(mid,14,endDate);
      setVolumeBars(_filterBars(r.data,co));
    }catch{}
  },[mid,market?.status,market?.resolved_at,market?.close_date]);

  /* Chargements initiaux — attendent market chargé */
  useEffect(()=>{
    if(!mid||!market)return;
    setHistLoading(true);
    const co=getCutoff(market,null);
    const endDate=co?new Date(co).toISOString().slice(0,10):null;
    fetchMarketHistory(mid,range,endDate)
      .then(r=>{
        const parsed=_parseApiHistory(r.data,null);
        const coWithHist=getCutoff(market,parsed);
        setHistory(coWithHist?_parseApiHistory(r.data,coWithHist):parsed);
      })
      .catch(()=>setHistory(h=>h.length?h:_fallbackHistory([],range)))
      .finally(()=>setHistLoading(false));
  },[mid,range,market?.status,market?.resolved_at,market?.close_date]);

  useEffect(()=>{if(!mid)return;setActLoading(true);fetchMarketTrades(mid).then(r=>{const p=_parseApiActivity(r.data);setActivity(p.length>0?p:[]);}).catch(()=>setActivity([])).finally(()=>setActLoading(false));},[mid]);

  useEffect(()=>{
    if(!mid||!market)return;
    setVolLoading(true);
    /* end_date = date de coupure en YYYY-MM-DD pour que le backend
       calcule les 14j EN ARRIÈRE depuis cette date, pas depuis aujourd'hui */
    const co=getCutoff(market,history.length>0?history:null);
    const endDate=co?new Date(co).toISOString().slice(0,10):null;
    fetchMarketVolume(mid,14,endDate)
      .then(r=>{
        setVolumeBars(_filterBars(r.data,co));
      })
      .catch(()=>setVolumeBars(Array.from({length:14},(_,i)=>({date:new Date(Date.now()-(13-i)*86400000).toISOString().slice(0,10),volume:0}))))
      .finally(()=>setVolLoading(false));
  },[mid,market?.status,market?.resolved_at,market?.close_date]);

  /* Polling — arrêté si marché non-OPEN */
  useEffect(()=>{
    if(!mid)return;
    if(market&&market.status!=="OPEN")return;
    const tick=async()=>{await Promise.allSettled([refreshMarket(),refreshHistory(),refreshActivity(),refreshVolume()]);setLastUpdate(new Date());};
    const id=setInterval(tick,POLL_INTERVAL);
    return()=>clearInterval(id);
  },[mid,market?.status,refreshMarket,refreshHistory,refreshActivity,refreshVolume]);

  /* Escape pour fermer modal */
  useEffect(()=>{
    if(!chartExpanded)return;
    const handler=e=>{if(e.key==="Escape")setChartExpanded(false);};
    window.addEventListener("keydown",handler);
    return()=>window.removeEventListener("keydown",handler);
  },[chartExpanded]);

  if(loading)return(<Wrapper><BackBtn onClick={onBack}>{mdt("back",lang)}</BackBtn><div style={{color:"#5a6a7a",fontFamily:"DM Mono",fontSize:".8rem"}}>{mdt("loading",lang)}</div></Wrapper>);
  if(!market)return(<Wrapper><BackBtn onClick={onBack}>{mdt("back",lang)}</BackBtn><div style={{color:"#5a6a7a",fontFamily:"DM Mono",fontSize:".8rem"}}>{mdt("notFound",lang)}</div></Wrapper>);

  const outcomes=market.outcomes||[];
  const isOpen=market.status==="OPEN";
  const emoji=EMOJI_MAP[market.category_name]||"🎯";
  const API=process.env.REACT_APP_API_URL||"";
  const fonctionAccederTrade=()=>{user?setTrading(true):onLoginClick();};

  return(
    <Wrapper dir={lang==="ar"?"rtl":"ltr"}>
      <BackBtn onClick={onBack}>{mdt("back",lang)}</BackBtn>
      <Hero>
        <Thumb
          onClick={market.image_url ? () => setImageExpanded(true) : undefined}
          style={market.image_url ? { cursor: "zoom-in" } : undefined}
        >
          {market.image_url?<img src={API+market.image_url} alt={market.title} style={{width:"100%",height:"100%",objectFit:"cover",borderRadius:"14px"}}/>:emoji}
        </Thumb>
        <HeroInfo>
          <HeroCat>{market.category_name}</HeroCat>
          <HeroTitle>{market.title}</HeroTitle>
          <BadgeRow>
            <Badge $g={market.status==="OPEN"} $r={market.status==="CANCELLED"} $b={market.status==="CLOSED"}>{market.status==="OPEN"&&"● "}{market.status}</Badge>
            <Badge>Vol {fmtVol(market.volume)}</Badge>
            <Badge>Liq {fmtVol(market.liquidity)}</Badge>
            {market.resolved_outcome&&<Badge $g>✓ {mdt("resolvedBadge",lang)}: {market.resolved_outcome}</Badge>}
          </BadgeRow>
        </HeroInfo>
      </Hero>

      <Layout>
        <Left>
          <Panel $d={0.05}>
            <PanelHead>
              <PanelTitle>
                {mdt("probHist",lang)}
                {lastUpdate&&isOpen&&<LiveBadge>{mdt("live",lang)}</LiveBadge>}
              </PanelTitle>
              <div style={{display:"flex",gap:".4rem",alignItems:"center",flexWrap:"wrap"}}>
                <ChartToggleRow>
                  <ChartToggleBtn $active={chartType==="line"} onClick={()=>setChartType("line")}>{mdt("chartLine",lang)}</ChartToggleBtn>
                  <ChartToggleBtn $active={chartType==="candle"} onClick={()=>setChartType("candle")}>🕯 {mdt("chartCandle",lang)}</ChartToggleBtn>
                </ChartToggleRow>
                <RangeTabs>{[7,14,30].map(r=><RangeBtn key={r} $active={range===r} onClick={()=>setRange(r)}>{r}D</RangeBtn>)}</RangeTabs>
                <ExpandBtn onClick={()=>setChartExpanded(true)} title="Agrandir">⛶</ExpandBtn>
              </div>
            </PanelHead>
            {histLoading
              ?<div style={{height:200,background:"#141820",borderRadius:8}}/>
              :chartType==="candle"
              ?<CandlestickChart history={history} outcomes={outcomes} lang={lang}/>
              :<LineChart history={history} outcomes={outcomes} lang={lang}/>
            }
          </Panel>

          <Panel $d={0.1}>
            <PanelTitle style={{marginBottom:"1rem"}}>
              {mdt("currProb",lang)}{lastUpdate&&isOpen&&<LiveBadge>{mdt("live",lang)}</LiveBadge>}
            </PanelTitle>
            <OutcomeList>
              {outcomes.map((o,i)=>{
                const pct=Math.round(parseFloat(o.probability)*100);
                return(<OutcomeRow key={o.id}>
                  <OutcomeTop><OutcomeName>{o.name}</OutcomeName><OutcomePct $col={COLORS[i]}>{pct}%</OutcomePct></OutcomeTop>
                  <BarTrack><BarFill style={{width:`${pct}%`,background:COLORS[i]}}/></BarTrack>
                  <OutcomeMeta>
                    <span>{fmtShares(o.shares_outstanding)} {mdt("shares",lang)}</span>
                    <span>{pct}% {mdt("probability",lang)}</span>
                    <span>{mdt("returnPer",lang)}: {(100/pct).toFixed(2)} EVP</span>
                  </OutcomeMeta>
                </OutcomeRow>);
              })}
            </OutcomeList>
          </Panel>

          <Panel $d={0.15}>
            <PanelTitle style={{marginBottom:"1rem"}}>{mdt("dailyVol",lang)}</PanelTitle>
            <VolumeChart bars={volumeBars} loading={volLoading} lang={lang} cutoffDate={cutoff}/>
          </Panel>

          {market.description&&(
            <Panel $d={0.2}>
              <PanelTitle style={{marginBottom:".75rem"}}>{mdt("resolution",lang)}</PanelTitle>
              <Desc>{market.description}</Desc>
            </Panel>
          )}
        </Left>

        <Right>
          <TradePanel $d={0.05}>
            <PanelTitle style={{marginBottom:".75rem"}}>{mdt("trade",lang)}</PanelTitle>
            {isOpen?(
              <>
                <QuickBuy>
                  {outcomes.slice(0,2).map((o,i)=>(
                    <QuickBtn key={o.id} $green={i===0} onClick={fonctionAccederTrade}>
                      {mdt("buy",lang||"fr")??"Acheter"} {o.name}<br/>
                      <span style={{fontSize:".65rem",opacity:.7}}>{Math.round(parseFloat(o.probability)*100)}%</span>
                    </QuickBtn>
                  ))}
                </QuickBuy>
                <TradeBtnBig onClick={fonctionAccederTrade}>{mdt("openTrade",lang)}</TradeBtnBig>
                {!user&&<div style={{fontSize:".68rem",color:"#5a6a7a",textAlign:"center",marginTop:".5rem"}}>{mdt("signIn",lang)}</div>}
              </>
            ):(
              <>
                <TradeBtnBig disabled>{market.status==="RESOLVED"?mdt("marketResolved",lang):mdt("marketClosed",lang)}</TradeBtnBig>
                <div style={{fontSize:".68rem",color:"#5a6a7a",textAlign:"center",marginTop:".5rem"}}>
                  {market.status==="RESOLVED"?`${mdt("winnerLabel",lang)}: ${market.resolved_outcome||"—"}`:mdt("noMoreTrades",lang)}
                </div>
              </>
            )}
          </TradePanel>

          <Panel $d={0.1}>
            <PanelTitle style={{marginBottom:".75rem"}}>{mdt("marketStats",lang)}</PanelTitle>
            <StatGrid>
              <StatBox><StatVal>{fmtVol(market.volume)}</StatVal><StatLbl>{mdt("volume",lang)}</StatLbl></StatBox>
              <StatBox><StatVal style={{color:"#4fc3f7"}}>{fmtVol(market.liquidity)}</StatVal><StatLbl>{mdt("liquidity",lang)}</StatLbl></StatBox>
              <StatBox><StatVal>{outcomes.length}</StatVal><StatLbl>{mdt("outcomes",lang)}</StatLbl></StatBox>
              <StatBox><StatVal>{fmtShares(outcomes.reduce((s,o)=>s+parseFloat(o.shares_outstanding||0),0))}</StatVal><StatLbl>{mdt("shares",lang)}</StatLbl></StatBox>
            </StatGrid>
          </Panel>

          <Panel $d={0.15}>
            <PanelTitle style={{marginBottom:".75rem"}}>{mdt("keyDates",lang)}</PanelTitle>
            <DateList>
              <DateRow><DateKey>{mdt("created",lang)}</DateKey><DateVal>{fmtDate(market.created_at,lang)}</DateVal></DateRow>
              <DateRow><DateKey>{mdt("closes",lang)}</DateKey><DateVal>{fmtDate(market.close_date,lang)}</DateVal></DateRow>
              <DateRow><DateKey>{mdt("resolves",lang)}</DateKey><DateVal>{fmtDate(market.resolution_date,lang)}</DateVal></DateRow>
              {market.resolved_at&&<DateRow><DateKey>{mdt("resolved",lang)}</DateKey><DateVal>{fmtDate(market.resolved_at,lang)}</DateVal></DateRow>}
            </DateList>
          </Panel>

          <Panel $d={0.2}>
            <PanelTitle style={{marginBottom:".75rem"}}>
              {mdt("activity",lang)}
              {!actLoading&&activity.length>0&&<span style={{marginLeft:".5rem",color:"#5a6a7a",fontWeight:400,textTransform:"none",letterSpacing:"normal",fontSize:".6rem"}}>{activity.length} trades</span>}
              {lastUpdate&&isOpen&&<LiveBadge>{mdt("live",lang)}</LiveBadge>}
            </PanelTitle>
            {actLoading?(
              <div style={{display:"flex",flexDirection:"column",gap:".4rem"}}>{[1,2,3].map(i=><div key={i} style={{height:32,background:"#141820",borderRadius:6}}/>)}</div>
            ):activity.length===0?(
              <div style={{fontSize:".75rem",color:"#5a6a7a",textAlign:"center",padding:"1rem"}}>{mdt("noActivity",lang)}</div>
            ):(
              <ActivityList>
                {activity.map((a,i)=>(
                  <ActivityRow key={i}>
                    <ActivityDot $buy={a.buy}/>
                    <ActivityText><strong>{a.user}</strong> {a.buy?mdt("bought",lang):mdt("sold",lang)} <strong>{a.outcome}</strong></ActivityText>
                    <ActivityAmt $buy={a.buy}>{a.amount} EVP</ActivityAmt>
                    <ActivityTime>{a.time}</ActivityTime>
                  </ActivityRow>
                ))}
              </ActivityList>
            )}
          </Panel>
        </Right>
      </Layout>

      <FullPanel $d={0.25} ref={commentsRef}>
        <CommentsSection marketId={market.id} user={user} onLoginClick={onLoginClick} pollInterval={POLL_INTERVAL}/>
      </FullPanel>

      {trading&&<TradeModal market={market} user={user} onLoginClick={onLoginClick} initialSide={autoTradeInit?.side} initialOutcomeId={autoTradeInit?.outcomeId} onClose={()=>{setTrading(false);setAutoTradeInit(null);}}/>}

      {/* ── Barre de navigation basse (mobile uniquement) ── */}
      <MarketDetailBottomBar
        isOpen={isOpen}
        lang={lang}
        commentCount={activity.length}
        onMarkets={onBack}
        onDiscussion={()=>commentsRef.current?.scrollIntoView({behavior:"smooth",block:"start"})}
        onTrade={fonctionAccederTrade}
      />

      {chartExpanded&&(
        <ChartModalOverlay onClick={e=>e.target===e.currentTarget&&setChartExpanded(false)}>
          <ChartModalBox>
            <ChartModalHead>
              <ChartModalTitle>
                {mdt("probHist",lang)}
                {lastUpdate&&isOpen&&<LiveBadge>{mdt("live",lang)}</LiveBadge>}
                <span style={{color:"#3a4a5a",fontWeight:400,textTransform:"none",letterSpacing:"normal"}}>— {market.title}</span>
              </ChartModalTitle>
              <div style={{display:"flex",gap:".5rem",alignItems:"center",flexWrap:"wrap"}}>
                <ChartToggleRow>
                  <ChartToggleBtn $active={chartType==="line"} onClick={()=>setChartType("line")}>{mdt("chartLine",lang)}</ChartToggleBtn>
                  <ChartToggleBtn $active={chartType==="candle"} onClick={()=>setChartType("candle")}>🕯 {mdt("chartCandle",lang)}</ChartToggleBtn>
                </ChartToggleRow>
                <RangeTabs>{[7,14,30].map(r=><RangeBtn key={r} $active={range===r} onClick={()=>setRange(r)}>{r}D</RangeBtn>)}</RangeTabs>
                <ChartModalClose onClick={()=>setChartExpanded(false)}>✕ Fermer</ChartModalClose>
              </div>
            </ChartModalHead>
            <ChartModalBody>
              {histLoading
                ?<div style={{height:340,background:"#141820",borderRadius:8}}/>
                :chartType==="candle"
                ?<CandlestickChart history={history} outcomes={outcomes} lang={lang}/>
                :<LineChart history={history} outcomes={outcomes} lang={lang}/>
              }
            </ChartModalBody>
          </ChartModalBox>
        </ChartModalOverlay>
      )}

      {imageExpanded && market.image_url && (
        <ChartModalOverlay onClick={e=>e.target===e.currentTarget&&setImageExpanded(false)}>
          <div style={{position:"relative",maxWidth:"90vw",maxHeight:"90vh"}}>
            <ChartModalClose
              onClick={()=>setImageExpanded(false)}
              style={{position:"absolute",top:"-2.5rem",right:0}}
            >
              ✕ Fermer
            </ChartModalClose>
            <img
              src={API+market.image_url}
              alt={market.title}
              style={{maxWidth:"90vw",maxHeight:"90vh",borderRadius:"12px",display:"block",objectFit:"contain"}}
            />
          </div>
        </ChartModalOverlay>
      )}
    </Wrapper>
  );
}
